import React, { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Badge,
} from 'react-bootstrap';
import {
  FaCalendarAlt,
  FaTimes,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
} from 'react-icons/fa';
import AttendanceService from '../../../../services/attendanceService';

const StudentHistoryModal = ({ student, batchId, onClose }) => {
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    loadAttendanceHistory();
  }, [student, batchId, currentYear, currentMonth]);

  const loadAttendanceHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Loading attendance for:', {
        studentId: student.id,
        batchId: batchId,
        year: currentYear,
        month: currentMonth + 1
      });
      
      const response = await AttendanceService.getStudentCalendar(
        student.id,
        batchId,
        currentYear,
        currentMonth + 1
      );
      
      console.log('Calendar API Response:', response);
      
      if (response.success) {
        setAttendanceData(response.data);
      } else {
        setError(response.message || 'Failed to load attendance data');
      }
    } catch (error) {
      console.error('Failed to load attendance history:', error);
      setError(error.response?.data?.message || error.message || 'Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  const getDayClass = (dateStr, status) => {
    const today = new Date().toISOString().split('T')[0];
    if (dateStr === today) return 'today';
    if (status === 'absent') return 'absent';
    if (status === 'present') return 'present';
    return '';
  };

  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Empty cells for days before first day of month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push({ date: null, isEmpty: true });
    }

    // Days of the month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const attendance = attendanceData?.attendance?.[dateStr];
      const status = attendance?.status || null;
      
      days.push({
        date: d,
        dateStr: dateStr,
        status: status,
        class: getDayClass(dateStr, status),
        isEmpty: false
      });
    }

    return days;
  };

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Modal show={true} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header className="bg-primary text-white">
        <Modal.Title>
          <FaCalendarAlt className="me-2" />
          Attendance History - {student?.name}
        </Modal.Title>
        <Button variant="link" className="text-white p-0" onClick={onClose}>
          <FaTimes size={20} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 text-muted">Loading attendance data...</p>
          </div>
        ) : error ? (
          <Alert variant="danger" className="text-center">
            <p>{error}</p>
            <Button variant="outline-danger" size="sm" onClick={loadAttendanceHistory}>
              Try Again
            </Button>
          </Alert>
        ) : attendanceData ? (
          <>
            {/* Student Info */}
            <Alert variant="info" className="mb-3">
              <strong>{student?.name}</strong> - {student?.email}
              <br />
              <small>Batch: {attendanceData.batch?.name}</small>
            </Alert>

            {/* Calendar Navigation */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Button variant="outline-secondary" size="sm" onClick={prevMonth}>
                ◀ Previous
              </Button>
              <h5 className="mb-0">
                {monthNames[currentMonth]} {currentYear}
              </h5>
              <div>
                <Button variant="outline-success" size="sm" className="me-2" onClick={goToToday}>
                  Today
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={nextMonth}>
                  Next ▶
                </Button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="border rounded p-3 bg-light">
              <div className="d-grid mb-2" style={{ gridTemplateColumns: 'repeat(7, 1fr)' }}>
                {weekdays.map(day => (
                  <div key={day} className="text-center fw-bold text-muted">
                    {day}
                  </div>
                ))}
              </div>
              <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: '5px' }}>
                {generateCalendar().map((day, index) => (
                  <div
                    key={index}
                    className={`text-center p-2 rounded ${
                      day.isEmpty 
                        ? '' 
                        : day.class === 'absent'
                        ? 'bg-danger text-white'
                        : day.class === 'present'
                        ? 'bg-success text-white'
                        : day.class === 'today'
                        ? 'bg-info text-white fw-bold'
                        : 'bg-white border'
                    }`}
                    style={{ minHeight: '45px' }}
                  >
                    {day.date}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="mt-3 d-flex justify-content-center gap-3 flex-wrap">
              <div><Badge bg="danger" className="me-1">●</Badge> Absent</div>
              <div><Badge bg="success" className="me-1">●</Badge> Present</div>
              <div><Badge bg="info" className="me-1">●</Badge> Today</div>
              <div><Badge bg="secondary" className="me-1">○</Badge> No Class</div>
            </div>

            {/* Summary Stats */}
            <Row className="mt-4">
              <Col md={4}>
                <Card className="text-center border-success">
                  <Card.Body>
                    <FaCheckCircle size={30} className="text-success mb-2" />
                    <h3 className="mb-0 text-success">{attendanceData.summary?.present_days || 0}</h3>
                    <small className="text-muted">Present Days</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center border-danger">
                  <Card.Body>
                    <FaTimesCircle size={30} className="text-danger mb-2" />
                    <h3 className="mb-0 text-danger">{attendanceData.summary?.absent_days || 0}</h3>
                    <small className="text-muted">Absent Days</small>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                <Card className="text-center border-primary">
                  <Card.Body>
                    <FaChartLine size={30} className="text-primary mb-2" />
                    <h3 className="mb-0 text-primary">{attendanceData.summary?.percentage || 0}%</h3>
                    <small className="text-muted">Attendance Percentage</small>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <div className="mt-3 text-center text-muted small">
              Total Classes: {attendanceData.summary?.total_days || 0}
            </div>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StudentHistoryModal;